import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { LoadingValueExtended } from '../services/firebase';
import { useLoadingValue } from './useLoadingValue';

export const usePromise = <T>(
  fn: (forceValue: (nextValue: T) => boolean) => Promise<T>,
  listeners: any[] = [],
  initialValue?: T
): LoadingValueExtended<T, any, () => void> => {
  // rev updates everytime a new value is requested.
  // allows as to make sure, that only the latest call is used
  // to update the value, avoiding potential race conditions, where
  // call A might return after call B and hence override the already
  // set value coming from B
  const [rev, setRev] = useState(0);
  const ref = useRef(0);
  const {
    value,
    error,
    loading,
    setLoading,
    setValue,
    setError
  } = useLoadingValue<T>(initialValue);

  const forceReload = useCallback(() => setRev((prev) => prev + 1), []);

  // useLayout effect, because if we want to go to another loading
  // state, the old value should not be flushed to them DOM!
  useLayoutEffect(() => {
    if (!loading) {
      setLoading(true);
    }
    const v = ++ref.current;
    fn((t) => {
      if (v === ref.current) {
        setValue(t);
        return true;
      } else {
        return false;
      }
    }).then(
      (val) => {
        if (v === ref.current) {
          setValue(val);
        }
      },
      (err) => {
        if (v === ref.current) {
          setError(err);
        }
      }
    );
    // eslint-disable-next-line
  }, [...listeners, rev]);

  return [value, loading, error, forceReload];
};
